const STORAGE_KEY = "theme";
const DARK_CLASS = "dark";
const systemTheme = window.matchMedia("(prefers-color-scheme: dark)");
const themeToggles = Array.from(
  document.querySelectorAll<HTMLElement>("[data-theme-toggle]"),
);

type ThemePreference = "light" | "dark";

function getForcedTheme(): ThemePreference | null {
  let value = document.documentElement.dataset.themeMode;
  return value === "light" || value === "dark" ? value : null;
}

function getStoredTheme(): ThemePreference | null {
  let value = localStorage.getItem(STORAGE_KEY);
  return value === "light" || value === "dark" ? value : null;
}

function prefersDark(): boolean {
  return systemTheme.matches;
}

function resolveTheme(): ThemePreference {
  return getForcedTheme() ?? getStoredTheme() ?? (prefersDark() ? "dark" : "light");
}

function updateThemeImages(theme: ThemePreference): void {
  let sourceKey = theme === "dark" ? "themeDarkSrc" : "themeLightSrc";

  for (let image of document.querySelectorAll<HTMLImageElement>(
    "[data-theme-image]",
  )) {
    let source = image.dataset[sourceKey];
    if (source) image.src = source;
  }
}

function applyTheme(theme: ThemePreference): void {
  let isDark = theme === "dark";
  let root = document.documentElement;

  root.classList.toggle(DARK_CLASS, isDark);
  root.classList.toggle("scheme-dark", isDark);
  root.classList.toggle("scheme-light", !isDark);
  updateThemeImages(theme);
}

function updateToggleLabels(): void {
  let isDark = document.documentElement.classList.contains(DARK_CLASS);
  let label = isDark
    ? "Schakel lichte modus in"
    : "Schakel donkere modus in";

  for (let toggle of themeToggles) {
    let labelElement = toggle.querySelector<HTMLElement>(
      "[data-theme-toggle-label]",
    );
    toggle.setAttribute("aria-pressed", String(isDark));
    toggle.setAttribute("aria-label", label);
    toggle.setAttribute("title", label);
    if (labelElement) labelElement.textContent = label;
  }
}

function initThemeToggles(): void {
  if (getForcedTheme()) return;

  for (let toggle of themeToggles) {
    toggle.addEventListener("click", () => {
      let isDark = !document.documentElement.classList.contains(DARK_CLASS);
      let nextTheme: ThemePreference = isDark ? "dark" : "light";
      localStorage.setItem(STORAGE_KEY, nextTheme);
      applyTheme(nextTheme);
      updateToggleLabels();
    });
  }
}

function initSystemThemeSync(): void {
  if (getForcedTheme()) return;

  systemTheme.addEventListener("change", () => {
    if (getStoredTheme()) return;
    applyTheme(resolveTheme());
    updateToggleLabels();
  });
}

function initTheme(): void {
  applyTheme(resolveTheme());
  initThemeToggles();
  initSystemThemeSync();
  updateToggleLabels();
}

initTheme();
